import React, { useEffect, useRef, useState } from "react";
import "./App.css";

import gameMusic1 from "./sounds/game-music-1.mp3";
import gameMusic2 from "./sounds/game-music-2.mp3";
import gameMusic3 from "./sounds/game-music-3.mp3";

import correctSound from "./sounds/correct.wav";
import gameOverSound from "./sounds/gameover.wav";
import loseLifeSound from "./sounds/lose-life.ogg";

const INITIAL_WORDS = [
  { word: "function", definition: "A block of code that performs a specific task" },
  { word: "variable", definition: "A named value that can change in a program" },
  { word: "compiler", definition: "A program that converts code into another form" },
  { word: "inheritance", definition: "When a class takes properties from another class" },
  { word: "recursion", definition: "A function that calls itself to solve a problem" },
  { word: "internet", definition: "A global network that connects computers" },
  { word: "array", definition: "A list that stores multiple values in order" },
  { word: "clock", definition: "A device that shows the time" },
  { word: "biology", definition: "The study of living organisms and life" },
  { word: "algorithm", definition: "A step-by-step set of instructions to solve problems" }
];

const GAME_MODES = {
  CLASSIC: "classic",
  CLASSIC_HARD: "classic_hard",
  TIMED: "timed",
};


const GAME_MUSIC_PLAYLIST = [gameMusic1, gameMusic2, gameMusic3];

function App() {
  const [words, setWords] = useState(INITIAL_WORDS);
  const [shuffledWords, setShuffledWords] = useState([]);
  const [activeDefs, setActiveDefs] = useState([]);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [fallSpeed, setFallSpeed] = useState(0.5);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameMode, setGameMode] = useState(GAME_MODES.CLASSIC);
  const [timeLeft, setTimeLeft] = useState(180);
  const [inputValue, setInputValue] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackClass, setFeedbackClass] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [showFormatHelp, setShowFormatHelp] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const nextWordIndex = useRef(0);
  const intervalRef = useRef(null);
  const alreadyMissedIds = useRef(new Set());
  const lastAnswerTime = useRef(0);
  const lastSpawnTime = useRef(0);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const speedIncreaseIntervalRef = useRef(null);
  const spawnLoopRef = useRef(null);
  const isPausedRef = useRef(false);


  const gameMusicRef = useRef(null);
  const correctSoundRef = useRef(null);
  const gameOverSoundRef = useRef(null);
  const loseLifeSoundRef = useRef(null);


  const SPAWN_COOLDOWN_MS = 500;
  const MIN_SPAWN_MS = 5500;
  const MAX_SPAWN_MS = 9500;
  const MIN_SPAWN_MS_HARD = 2500;
  const MAX_SPAWN_MS_HARD = 4500;

  const INITIAL_SPEED = 0.45;
  const SPEED_INCREASE_AMOUNT = 0.15;
  const SPEED_INCREASE_INTERVAL = 5000;
  const MAX_SPEED = 2.3;

  /* -------------------- RANDOM MUSIC SELECTOR -------------------- */
  const getRandomTrack = (playlist) => {
    const randomIndex = Math.floor(Math.random() * playlist.length);
    return playlist[randomIndex];
  };

  /* -------------------- AUDIO SETUP -------------------- */
  useEffect(() => {
    correctSoundRef.current = new Audio(correctSound);
    gameOverSoundRef.current = new Audio(gameOverSound);
    loseLifeSoundRef.current = new Audio(loseLifeSound);

    correctSoundRef.current.volume = 0.7;
    gameOverSoundRef.current.volume = 0.7;
    loseLifeSoundRef.current.volume = 0.7;

    return () => {
      gameMusicRef.current?.pause();
      loseLifeSoundRef.current?.pause();
    };
  }, []);

  /* -------------------- MUTE CONTROL -------------------- */
  useEffect(() => {
    if (gameMusicRef.current) gameMusicRef.current.muted = isMuted;
  }, [isMuted]);


  /* -------------------- GAME MUSIC CONTROL -------------------- */
  useEffect(() => {
    if (gameStarted && !gameOver) {
      gameMusicRef.current?.pause();
      
      const randomGameTrack = getRandomTrack(GAME_MUSIC_PLAYLIST);
      gameMusicRef.current = new Audio(randomGameTrack);
      gameMusicRef.current.loop = true;
      gameMusicRef.current.volume = 0.25;
      gameMusicRef.current.muted = isMuted;
      
      gameMusicRef.current.play();
    } else {
      
      gameMusicRef.current?.pause();
      if (gameMusicRef.current) gameMusicRef.current.currentTime = 0;
    }
    
    return () => {
      if (gameStarted && !gameOver) {
        gameMusicRef.current?.pause();
      }
    };
  }, [gameStarted, gameOver]);

  /* -------------------- PAUSE/RESUME MUSIC -------------------- */
  useEffect(() => {
    if (gameStarted && !gameOver) {
      if (isPaused) {
        gameMusicRef.current?.pause();
      } else {
        gameMusicRef.current?.play();
      }
    }
  }, [isPaused, gameStarted, gameOver]);

  /* -------------------- STARFIELD -------------------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    starsRef.current = Array.from({ length: 200 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 2,
      speed: 0.5 + Math.random() * 1.5,
    }));

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "white";
      starsRef.current.forEach((star) => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
        star.y += star.speed;
        if (star.y > canvas.height) {
          star.y = 0;
          star.x = Math.random() * canvas.width;
        }
      });
      requestAnimationFrame(animate);
    }
    animate();

    return () => window.removeEventListener("resize", resize);
  }, []);

  /* -------------------- PAUSE ON TAB -------------------- */
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) {
        setIsPaused(true);
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  /* -------------------- PAUSE / QUIT CONFIRM WITH ESC -------------------- */
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key !== "Escape") return;
      if (!gameStarted || gameOver) return;

      if (showQuitConfirm) {
        setShowQuitConfirm(false);
        setIsPaused(false);
        lastAnswerTime.current = Date.now();
        return;
      }

      setIsPaused((prev) => {
        const next = !prev;
        if (!next) lastAnswerTime.current = Date.now();
        return next;
      });
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameStarted, gameOver, showQuitConfirm]);

  /* -------------------- HARD MODE SPEED INCREASE -------------------- */
  useEffect(() => {
    if (!gameStarted || gameOver || isPaused || gameMode !== GAME_MODES.CLASSIC_HARD) return;

    speedIncreaseIntervalRef.current = setInterval(() => {
      setFallSpeed((prev) => Math.min(prev + SPEED_INCREASE_AMOUNT, MAX_SPEED));
    }, SPEED_INCREASE_INTERVAL);

    return () => clearInterval(speedIncreaseIntervalRef.current);
  }, [gameStarted, gameOver, isPaused, gameMode]);

  useEffect(() => {
    if (gameMode === GAME_MODES.CLASSIC_HARD && gameStarted && !gameOver) {
      setActiveDefs((defs) => defs.map((d) => ({ ...d, speed: fallSpeed })));
    }
  }, [fallSpeed, gameMode, gameStarted, gameOver]);

  /* -------------------- SPAWN DEFINITION -------------------- */
  const spawnDefinition = React.useCallback(() => {
    const now = Date.now();
    if (now - lastAnswerTime.current < SPAWN_COOLDOWN_MS) return;

    const minSpawn = gameMode === GAME_MODES.CLASSIC_HARD ? MIN_SPAWN_MS_HARD : MIN_SPAWN_MS;
    if (now - lastSpawnTime.current < minSpawn) return;

    if (
      (gameMode === GAME_MODES.CLASSIC || gameMode === GAME_MODES.CLASSIC_HARD) &&
      nextWordIndex.current >= shuffledWords.length
    )
      return;

    const w = shuffledWords[nextWordIndex.current % shuffledWords.length];
    if (!w) return;

    const boxWidth = Math.min(400, window.innerWidth - 20);
    const padding = boxWidth / 2;

    setActiveDefs((defs) => [
      ...defs,
      {
        id: Date.now() + Math.random(),
        word: w.word,
        definition: w.definition,
        y: 0,
        speed: fallSpeed,
        x: Math.random() * (window.innerWidth - padding * 2) + padding,
      },
    ]);

    lastSpawnTime.current = now;
    nextWordIndex.current += 1;
  }, [shuffledWords, fallSpeed, gameMode]);

  /* -------------------- FALLING LOOP -------------------- */
  useEffect(() => {
    if (!gameStarted || gameOver || isPaused) return;

    intervalRef.current = setInterval(() => {
      setActiveDefs((defs) => {
        const removeIds = [];
        const updated = defs.map((d) => ({ ...d, y: d.y + d.speed }));

        for (let d of updated) {
          if (d.y > window.innerHeight - 120) {
            if (!alreadyMissedIds.current.has(d.id)) {
              alreadyMissedIds.current.add(d.id);
              if (
                gameMode === GAME_MODES.CLASSIC ||
                gameMode === GAME_MODES.CLASSIC_HARD
              ) {
                setLives((prev) => {
                  const next = prev - 1;

                  loseLifeSoundRef.current.currentTime = 0;
                  loseLifeSoundRef.current.play()
                  if (next <= 0) endGame();
                  return next;
                });
              }
            }
            removeIds.push(d.id);
          }
        }

        const newDefs = updated.filter((d) => !removeIds.includes(d.id));

        if (
          (gameMode === GAME_MODES.CLASSIC ||
            gameMode === GAME_MODES.CLASSIC_HARD) &&
          lives <= 0
        ) {
          endGame();
        }

        return newDefs;
      });
    }, 16);

    return () => clearInterval(intervalRef.current);
  }, [gameStarted, gameOver, gameMode, isPaused, shuffledWords.length, lives]);

  /* -------------------- CHECK GAME COMPLETION -------------------- */
  useEffect(() => {
    if (!gameStarted || gameOver || gameMode === GAME_MODES.TIMED) return;

    if (nextWordIndex.current >= shuffledWords.length && activeDefs.length === 0) {
      endGame();
    }
  }, [activeDefs.length, gameStarted, gameOver, gameMode, shuffledWords.length]);

  /* -------------------- SPAWN LOOP -------------------- */
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const startSpawnLoop = React.useCallback(() => {
    const loop = () => {
      if (!gameStarted || gameOver) return;
      if (!isPausedRef.current) spawnDefinition();

      const minSpawn =
        gameMode === GAME_MODES.CLASSIC_HARD ? MIN_SPAWN_MS_HARD : MIN_SPAWN_MS;
      const maxSpawn =
        gameMode === GAME_MODES.CLASSIC_HARD ? MAX_SPAWN_MS_HARD : MAX_SPAWN_MS;
      const nextTime = Math.random() * (maxSpawn - minSpawn) + minSpawn;

      spawnLoopRef.current = setTimeout(loop, nextTime);
    };
    loop();
  }, [gameStarted, gameOver, spawnDefinition, gameMode]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    startSpawnLoop();
    return () => clearTimeout(spawnLoopRef.current);
  }, [gameStarted, gameOver, startSpawnLoop]);

  /* -------------------- TIMED MODE -------------------- */
  useEffect(() => {
    if (!gameStarted || gameOver || isPaused || gameMode !== GAME_MODES.TIMED) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          endGame();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [gameStarted, gameOver, gameMode, isPaused]);

  /* -------------------- AUTO CLEAR FEEDBACK -------------------- */
  useEffect(() => {
    if (!feedbackText) return;
    setIsFading(false);

    const fadeTimer = setTimeout(() => {
      setIsFading(true);
    }, 4500);

    const clearTimer = setTimeout(() => {
      setFeedbackText("");
      setFeedbackClass("");
      setIsFading(false);
    }, 5000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
    };
  }, [feedbackText]);

  /* -------------------- GAME CONTROL -------------------- */
  function startGame() {
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    setShuffledWords(shuffled);
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setLives(3);
    setActiveDefs([]);
    setTimeLeft(180);
    setIsPaused(false);
    setShowQuitConfirm(false);
    setFallSpeed(gameMode === GAME_MODES.CLASSIC_HARD ? INITIAL_SPEED : 0.5);
    nextWordIndex.current = 0;
    alreadyMissedIds.current = new Set();
    lastSpawnTime.current = 0;
    setInputValue("");
    setFeedbackText("");
    setFeedbackClass("");
  }

  function endGame() {
    clearInterval(intervalRef.current);
    clearInterval(timerRef.current);
    clearTimeout(spawnLoopRef.current);
    clearInterval(speedIncreaseIntervalRef.current);
    setGameOver(true);
    
    gameOverSoundRef.current?.play();
  }

  function goToMainMenu() {
    setGameStarted(false);
    setGameOver(false);
    setActiveDefs([]);
    setInputValue("");
    setFeedbackText("");
    setFeedbackClass("");
    setIsPaused(false);
    setShowQuitConfirm(false);
    clearTimeout(spawnLoopRef.current);
    clearInterval(speedIncreaseIntervalRef.current);
  }

  function handleKeyDown(e) {
    if (e.key !== "Enter" || isPaused) return;

    const typed = inputValue.toLowerCase();
    const match = activeDefs.find((d) => d.word.toLowerCase() === typed);

    if (match) {
      lastAnswerTime.current = Date.now();
      setActiveDefs((defs) => {
        const newDefs = defs.filter((d) => d.id !== match.id);
        return newDefs;
      });
      setScore((s) => s + 1);
      setFeedbackText("Correct!");
      setFeedbackClass("correct");
      
      correctSoundRef.current.currentTime = 0;
      correctSoundRef.current?.play();
    } else {
      setFeedbackText("Incorrect!");
      setFeedbackClass("incorrect");
    }

    setInputValue("");
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const newWords = ev.target.result
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((line) => {
          const [word, ...def] = line.split(":");
          return { word: word.trim(), definition: def.join(":").trim() };
        });

      if (newWords.length) setWords(newWords);
    };
    reader.readAsText(file);
  }

  /* -------------------- RENDER -------------------- */
  return (
    <>
      <canvas id="starfield" ref={canvasRef} />

      {!gameStarted && (
        <>
          <h1>Typing Study Game</h1>
          <select
            id="modeSelect"
            value={gameMode}
            onChange={(e) => setGameMode(e.target.value)}
          >
            <option value={GAME_MODES.CLASSIC}>Classic</option>
            <option value={GAME_MODES.CLASSIC_HARD}>Classic: Hard Mode</option>
            <option value={GAME_MODES.TIMED}>Timed (3 minutes)</option>
          </select>
          <button id="startBtn" onClick={startGame}>
            Start Game
          </button>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <label htmlFor="fileInput" style={{
              padding: '12px 30px',
              fontSize: '1.1em',
              backgroundColor: '#555',
              color: 'white',
              borderRadius: '10px',
              cursor: 'pointer',
              border: 'none',
              transition: 'background-color 0.2s'
            }}>
              📁 Choose File
            </label>
            <input
              type="file"
              id="fileInput"
              accept=".txt"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
          </div>

          <button 
            id="formatHelpBtn" 
            onClick={() => setShowFormatHelp(true)}
          >
            📚 File Format Guide
          </button>
        </>
      )}

      {gameStarted && !gameOver && (
        <>
          <button
            className="quit-btn"
            onClick={() => {
              setIsPaused(true);
              setShowQuitConfirm(true);
            }}
          >
            ❌
          </button>

          <button
            className="mute-btn"
            onClick={() => setIsMuted(!isMuted)}
          >
            {isMuted ? "🔈" : "🔊"}
          </button>

          {activeDefs
            .filter((d) => d.y <= window.innerHeight - 120)
            .map((d) => (
              <div
                key={d.id}
                className="definition-box"
                style={{
                  position: "fixed",
                  top: d.y,
                  left: d.x,
                  transform: "translateX(-50%)",
                }}
              >
                {d.definition}
              </div>
            ))}

          <input
            id="input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type the word and press Enter"
            autoFocus
          />

          <div
            id="feedback"
            className={`${feedbackClass} ${isFading ? "fade-out" : ""}`}
          >
            {feedbackText}
          </div>

          <div id="score">Score: {score}</div>

          {gameMode === GAME_MODES.TIMED && (
            <div
              style={{
                position: "absolute",
                bottom: "25px",
                left: "50%",
                transform: "translateX(220px)",
                fontSize: "1.75em",
                textShadow: "1px 1px 3px #000",
                color: "red",
              }}
            >
              Time: {Math.floor(timeLeft / 60)}:
              {String(timeLeft % 60).padStart(2, "0")}
            </div>
          )}

          {(gameMode === GAME_MODES.CLASSIC ||
            gameMode === GAME_MODES.CLASSIC_HARD) && (
            <div
              style={{
                position: "absolute",
                bottom: "25px",
                left: "50%",
                transform: "translateX(220px)",
                fontSize: "1.75em",
                textShadow: "1px 1px 3px #000",
                color: "red",
              }}
            >
              Lives: {"❤️".repeat(lives)}
            </div>
          )}
        </>
      )}

      {isPaused && gameStarted && !gameOver && !showQuitConfirm && (
        <div
          className="pause-overlay"
          onClick={() => {
            setIsPaused(false);
            lastAnswerTime.current = Date.now();
          }}
        >
          <div className="pause-box">
            <h2>GAME PAUSED</h2>
            <p>Click anywhere or press Escape to resume</p>
          </div>
        </div>
      )}

      {showQuitConfirm && gameStarted && !gameOver && (
        <div
          className="quit-confirm-overlay"
          onClick={() => {
            setShowQuitConfirm(false);
            setIsPaused(false);
          }}
        >
          <div
            className="quit-confirm-box"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Are you sure?</h3>
            <p>Quitting will return to main menu.</p>
            <div className="confirm-buttons">
              <button
                className="confirm-yes"
                onClick={() => {
                  setShowQuitConfirm(false);
                  goToMainMenu();
                }}
              >
                Yes
              </button>
              <button
                className="confirm-no"
                onClick={() => {
                  setShowQuitConfirm(false);
                  setIsPaused(false);
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showFormatHelp && (
        <div
          className="format-help-overlay"
          onClick={() => setShowFormatHelp(false)}
        >
          <div
            className="format-help-box"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>📚 File Format Guide</h2>
            <div className="format-content">
              <p className="format-intro">
                Your text file should contain one word-definition pair per line, 
                separated by a colon (:)
              </p>
              
              <div className="format-example">
                <h3>Example Format:</h3>
                <pre>
{`function: A block of code that performs a task
variable: A named value that can change
array: A list that stores multiple values
recursion: A function that calls itself`}
                </pre>
              </div>

              <div className="format-rules">
                <h3>Rules:</h3>
                <ul>
                  <li>Each line must have: <strong>word: definition</strong></li>
                  <li>Use a colon (:) to separate word and definition</li>
                  <li>One word-definition pair per line</li>
                  <li>Empty lines will be ignored</li>
                  <li>Definitions can contain colons - only the first one separates the word</li>
                </ul>
              </div>
            </div>

            <button
              className="format-close-btn"
              onClick={() => setShowFormatHelp(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {gameOver && (
        <div className="end-screen-overlay">
          <div className="end-screen">
            <h2>Game Over</h2>
            <p className="final-score">
              Final Score: {score}
              {(gameMode === GAME_MODES.CLASSIC ||
                gameMode === GAME_MODES.CLASSIC_HARD) &&
                ` / ${words.length}`}
            </p>
            <button id="restartBtn" onClick={startGame}>
              Restart Game
            </button>
            <button id="mainMenuBtn" onClick={goToMainMenu}>
              Back to Main Menu
            </button>
            <div className="file-section">
              <p>Want to study a different word list?</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                <label htmlFor="fileInputGameOver" style={{
                  padding: '12px 30px',
                  fontSize: '1.1em',
                  backgroundColor: '#555',
                  color: 'white',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: 'none',
                  transition: 'background-color 0.2s'
                }}>
                  📁 Choose File
                </label>
                <input
                  type="file"
                  id="fileInputGameOver"
                  accept=".txt"
                  onChange={handleFile}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;